/**
 * Utility functions
 * Migrated from utils.js
 */

/**
 * Format time in minutes to HH:MM string
 * @param {number} minutes - Total minutes from start
 * @param {Date} startTime - Optional start time (defaults to current time)
 * @returns {string} Formatted time string
 */
export const formatTime = (minutes, startTime = null) => {
  const base = startTime || new Date();
  const time = new Date(base.getTime() + minutes * 60000);
  return time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Format duration in minutes to readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration
 */
export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h${mins.toString().padStart(2, '0')}`;
};

/**
 * Fisher-Yates shuffle algorithm
 * @param {Array} array - Array to shuffle
 * @returns {Array} Shuffled array (new array, original unchanged)
 */
export const shuffle = (array) => {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

/**
 * Get element by ID
 */
export const $ = (id) => {
  return document.getElementById(id);
};

/**
 * Query selector all
 */
export const qsa = (selector, parent = document) => {
  return Array.from(parent.querySelectorAll(selector));
};

/**
 * Show element
 */
export const show = (element) => {
  if (element) {
    element.classList.remove('d-none');
  }
};

/**
 * Hide element
 */
export const hide = (element) => {
  if (element) {
    element.classList.add('d-none');
  }
};

/**
 * Toggle visibility of an element
 * @param {HTMLElement} element - Element to toggle
 * @param {boolean} show - Whether to show or hide
 */
export const toggle = (element, shouldShow) => {
  if (shouldShow) {
    show(element);
  } else {
    hide(element);
  }
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Calculate maximum theoretical rounds without repetition
 * @param {number} playerCount - Number of players
 * @returns {number} Maximum rounds
 */
export const maxRoundsWithoutRepetition = (playerCount) => {
  return Math.floor((playerCount - 1) / 3);
};

/**
 * Calculate effective court count based on players
 * @param {number} playerCount - Number of players
 * @param {number} courtCount - Requested court count
 * @returns {number} Effective court count
 */
export const effectiveCourtCount = (playerCount, courtCount) => {
  const maxCourts = Math.floor(playerCount / 4);
  return Math.min(courtCount, maxCourts);
};

/**
 * Generate unique ID
 * @returns {string}
 */
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * Deep clone an object
 * @param {Object} obj - Object to clone
 * @returns {Object}
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Save to localStorage
 */
export const saveToStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save to localStorage:', e);
  }
};

/**
 * Load from localStorage
 */
export const loadFromStorage = (key, defaultValue = null) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : defaultValue;
  } catch (e) {
    console.error('Failed to load from localStorage:', e);
    return defaultValue;
  }
};

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
export const escapeHtml = (str) => {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};
